/**
 * FeedbackAttachmentsField — adjuntos de un ticket del Feedback Hub.
 *
 * Sube los ficheros en cuanto se eligen (el ticket puede no existir todavía) y
 * avisa al padre con el registro creado, para que lo vincule al guardar. Si el
 * ticket ya existe (`feedbackId`), además se vincula en el momento.
 */
import React, { useRef, useState } from 'react';
import api from '../services/api';
import './RichTextEditor.css';

export function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentChip({ attachment, onRemove }) {
  // Las imágenes se abren inline (previsualización) y el resto se descarga.
  const href = attachment.is_image
    ? attachment.inline_url || attachment.url
    : attachment.url || attachment.inline_url;
  return (
    <span className="feedback-attachment-chip">
      {attachment.is_image ? (
        <img src={attachment.inline_url || attachment.url} alt={attachment.original_name} />
      ) : (
        <span aria-hidden="true">📄</span>
      )}
      <a
        className="feedback-attachment-name"
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        title={attachment.original_name}
      >
        {attachment.original_name}
      </a>
      {typeof attachment.size_bytes === 'number' && (
        <span className="text-muted">{formatSize(attachment.size_bytes)}</span>
      )}
      {onRemove && (
        <button type="button" onClick={() => onRemove(attachment)} title="Quitar adjunto">
          ×
        </button>
      )}
    </span>
  );
}

export default function FeedbackAttachmentsField({
  companyId,
  attachments = [],
  onUploaded,
  onRemoved,
  feedbackId,
  onError,
  disabled = false,
  hint = 'Puedes adjuntar capturas, logs, PDFs…',
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(0);

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    if (!companyId) {
      onError?.('Selecciona una empresa antes de adjuntar ficheros.');
      return;
    }

    for (const file of files) {
      setUploading((count) => count + 1);
      try {
        // eslint-disable-next-line no-await-in-loop
        const attachment = await api.uploadFeedbackAttachment(file, companyId, { feedbackId });
        onUploaded?.(attachment);
      } catch (err) {
        onError?.(err.message || `No se pudo subir ${file.name}`);
      } finally {
        setUploading((count) => Math.max(0, count - 1));
      }
    }
  };

  return (
    <div className="feedback-attachments">
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || uploading > 0 || !companyId}
        >
          <span aria-hidden="true">📎</span>{' '}
          {uploading > 0 ? 'Subiendo…' : 'Adjuntar ficheros'}
        </button>
        {hint && <small className="text-muted">{hint}</small>}
      </div>

      {attachments.length > 0 && (
        <div className="feedback-attachments-list">
          {attachments.map((attachment) => (
            <AttachmentChip key={attachment.id} attachment={attachment} onRemove={onRemoved} />
          ))}
        </div>
      )}

      <input
        type="file"
        multiple
        ref={inputRef}
        className="d-none"
        onChange={handleFiles}
        tabIndex={-1}
      />
    </div>
  );
}
